import type { AttachmentContentsResponse } from './api';


/**
 * Simple cache service using the Cache API for caching attachment contents (images)
 */
export class AttachmentCacheService {
    private static readonly CACHE_NAME = 'attachment-cache';
    private static readonly CACHE_EXPIRY_TIME = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    private static readonly MAX_CACHE_SIZE = 500 * 1024 * 1024; // 500MB max cache size
    private static readonly MAX_ENTRIES = 100; // Maximum number of cache entries
    private static readonly FALLBACK_ENTRY_SIZE = 2 * 1024 * 1024; // Estimate size of 2MB for entries without size header

    /**
     * Get cached attachment contents for given attachment IDs
     * @param attachmentIds - Array of attachment IDs to check in cache
     * @returns Promise<AttachmentContentsResponse | null> - Cached data or null if not found/expired
     */
    static async getCachedAttachments(attachmentIds: string[]): Promise<AttachmentContentsResponse | null> {
        try {
            const cache = await caches.open(this.CACHE_NAME);
            const cacheKey = this.generateCacheKey(attachmentIds);
            const cachedResponse = await cache.match(cacheKey);

            if (!cachedResponse) {
                return null;
            }

            // Check if cache is expired
            const cacheDate = cachedResponse.headers.get('cache-date');
            if (cacheDate) {
                const cacheTime = new Date(cacheDate).getTime();
                const now = Date.now();

                if (now - cacheTime > this.CACHE_EXPIRY_TIME) {
                    // Cache expired, remove it
                    await cache.delete(cacheKey);
                    return null;
                }
            }

            const cachedData = await cachedResponse.json();
            console.log('✅ Cache hit for attachment IDs:', attachmentIds);
            return cachedData;
        } catch (error) {
            console.warn('Failed to get cached attachments:', error);
            return null;
        }
    }

    /**
     * Cache attachment contents
     * @param attachmentIds - Array of attachment IDs
     * @param attachmentContents - The attachment contents to cache
     */
    static async cacheAttachments(attachmentIds: string[], attachmentContents: AttachmentContentsResponse): Promise<void> {
        try {
            const cache = await caches.open(this.CACHE_NAME);
            const cacheKey = this.generateCacheKey(attachmentIds);

            // Check cache size limits before adding new entry
            await this.enforcesCacheLimits(cache);

            // Create a response with cache metadata
            const responseData = JSON.stringify(attachmentContents);
            const response = new Response(responseData, {
                headers: {
                    'Content-Type': 'application/json',
                    'cache-date': new Date().toISOString(),
                    'cache-size': responseData.length.toString()
                }
            });

            await cache.put(cacheKey, response);
            console.log('✅ Cached attachment contents for IDs:', attachmentIds);
        } catch (error) {
            console.warn('Failed to cache attachments:', error);
        }
    }

    /**
     * Clear all cached attachments
     */
    static async clearCache(): Promise<void> {
        try {
            await caches.delete(this.CACHE_NAME);
            console.log('✅ Attachment cache cleared');
        } catch (error) {
            console.warn('Failed to clear attachment cache:', error);
        }
    }

    /**
     * Get cache statistics for debugging
     */
    static async getCacheStats(): Promise<{ entryCount: number; totalSize: number; maxSize: number; maxEntries: number }> {
        try {
            const cache = await caches.open(this.CACHE_NAME);
            const requests = await cache.keys();
            const totalSize = await this.calculateCacheSize(cache, requests);

            return {
                entryCount: requests.length,
                totalSize,
                maxSize: this.MAX_CACHE_SIZE,
                maxEntries: this.MAX_ENTRIES
            };
        } catch (error) {
            console.warn('Failed to get cache stats:', error);
            return {
                entryCount: 0,
                totalSize: 0,
                maxSize: this.MAX_CACHE_SIZE,
                maxEntries: this.MAX_ENTRIES
            };
        }
    }

    /**
     * Generate a consistent cache key from attachment IDs
     * @param attachmentIds - Array of attachment IDs
     * @returns string - Cache key
     */
    private static generateCacheKey(attachmentIds: string[]): string {
        // Sort IDs to ensure consistent cache keys regardless of order
        const sortedIds = [...attachmentIds].sort((a, b) => a.localeCompare(b));
        return `attachments-${sortedIds.join(',')}`;
    }

    /**
     * Enforce cache size and entry limits by removing old entries
     * @param cache - The cache instance to manage
     */
    private static async enforcesCacheLimits(cache: Cache): Promise<void> {
        try {
            const requests = await cache.keys();

            // Check entry count limit
            if (requests.length >= this.MAX_ENTRIES) {
                await this.removeOldestEntries(cache, requests, Math.floor(this.MAX_ENTRIES * 0.2)); // Remove 20% of entries
            }

            // Check total cache size
            const totalSize = await this.calculateCacheSize(cache, requests);
            if (totalSize > this.MAX_CACHE_SIZE) {
                await this.removeEntriesUntilSizeLimit(cache, requests);
            }
        } catch (error) {
            console.warn('Failed to enforce cache limits:', error);
        }
    }

    /**
     * Calculate total size of all cache entries
     */
    private static async calculateCacheSize(cache: Cache, requests: readonly Request[]): Promise<number> {
        let totalSize = 0;

        for (const request of requests) {
            try {
                const response = await cache.match(request);
                if (response) {
                    const sizeHeader = response.headers.get('cache-size');
                    if (sizeHeader) {
                        totalSize += Number.parseInt(sizeHeader, 10);
                    } else {
                        // Fallback: estimate size as 2MB
                        totalSize += this.FALLBACK_ENTRY_SIZE;
                    }
                }
            } catch (error) {
                console.warn('Failed to calculate size for cache entry:', error);
            }
        }

        return totalSize;
    }

    /**
     * Remove oldest cache entries based on cache-date
     */
    private static async removeOldestEntries(cache: Cache, requests: readonly Request[], countToRemove: number): Promise<void> {
        const entriesWithDates: Array<{ request: Request; date: number }> = [];

        for (const request of requests) {
            try {
                const response = await cache.match(request);
                if (response) {
                    const dateHeader = response.headers.get('cache-date');
                    const date = dateHeader ? new Date(dateHeader).getTime() : 0;
                    entriesWithDates.push({ request, date });
                }
            } catch (error) {
                console.warn('Failed to get date for cache entry:', error);
            }
        }

        // Sort by date (oldest first) and remove the specified count
        entriesWithDates.sort((a, b) => a.date - b.date);
        const toRemove = entriesWithDates.slice(0, countToRemove);

        for (const entry of toRemove) {
            try {
                await cache.delete(entry.request);
                console.log('🗑️ Removed old cache entry due to entry limit');
            } catch (error) {
                console.warn('Failed to remove cache entry:', error);
            }
        }
    }

    /**
     * Remove entries until under size limit
     */
    private static async removeEntriesUntilSizeLimit(cache: Cache, requests: readonly Request[]): Promise<void> {
        const entriesWithData: Array<{ request: Request; date: number; size: number }> = [];

        for (const request of requests) {
            const entryData = await this.getCacheEntryData(cache, request);
            if (entryData) {
                entriesWithData.push(entryData);
            }
        }

        // Sort by date (oldest first)
        entriesWithData.sort((a, b) => a.date - b.date);

        let currentSize = entriesWithData.reduce((sum, entry) => sum + entry.size, 0);

        // Remove entries until we're under the size limit
        for (const entry of entriesWithData) {
            if (currentSize <= this.MAX_CACHE_SIZE * 0.8) { // Remove until 80% of limit to avoid frequent cleanup
                break;
            }

            try {
                await cache.delete(entry.request);
                currentSize -= entry.size;
                console.log('🗑️ Removed cache entry due to size limit');
            } catch (error) {
                console.warn('Failed to remove cache entry:', error);
            }
        }
    }

    private static async getCacheEntryData(
        cache: Cache,
        request: Request
    ): Promise<{ request: Request; date: number; size: number } | null> {
        try {
            const response = await cache.match(request);
            if (!response) {
                return null;
            }

            const dateHeader = response.headers.get('cache-date');
            const sizeHeader = response.headers.get('cache-size');
            const date = dateHeader ? new Date(dateHeader).getTime() : 0;
            const size = sizeHeader ? Number.parseInt(sizeHeader, 10) : this.FALLBACK_ENTRY_SIZE;
            return { request, date, size };
        } catch (error) {
            console.warn('Failed to get entry data:', error);
            return null;
        }
    }
}
